// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import './ScreenExperimentWizard.scss';
import ScreenWizardContext from './ScreenWizardContext';
import {
  ExperimentInfo,
  PlateTypeDTO,
  ProteinDetailDTO,
  ScreenExperimentTemplateDTO,
  ScreenInfoDTO,
} from 'business/models/Models';
import { getWizardDefaultPlateType, isNullOrUndefined } from 'utils/Global';
import useRockFetchGet, { useRockFetchPost } from 'utils/RockFetch';
import useBroadcast, { CHANNEL_TYPES } from 'business/broadcast';
import SelectPlateType from './SelectPlateType';
import { FmlxModal, FmlxWizard } from 'fmlx-common-ui';
import SelectScreenWizard from './SelectScreenWizard';
import SelectProteinWizard from './SelectProteinWizard';
import DefineParametersWizard from './DefineParametersWizard';
import ReviewExperiment from './ReviewExperiment';
import CreateExperiments from './CreateExperiments';

enum WizardSteps {
  SelectPlateType = 0,
  SelectScreen = 1,
  SelectProtein = 2,
  DefineParameters = 3,
  ReviewExperiment = 4,
  CreateExperiments = 5,
}

function ScreenExperimentWizard({
  onClose,
  commonTreeNodes,
  gSelectedNode,
  id = null,
  selectedParentNode,
}: any) {
  const [rockFetchPost] = useRockFetchPost();
  const [rockFetchGet] = useRockFetchGet();
  const experimentInfoList: ExperimentInfo[] = useMemo(() => [], []);
  const [screens, setScreens] = useState<ScreenInfoDTO[]>([]);
  const [proteins, setProteins] = useState([]);
  const [createExpPath, setCreateExpPath] = useState('');
  const [incubationTemp, setIncubationTemp] = useState([]);
  const [expTemplate, setExpTemplate] = useState(null);
  const [plateType, setPlateType] = useState(null);
  const [templateData, setTemplateData] = useState<ScreenExperimentTemplateDTO>(null);
  const [expSaveLocation, setExpSaveLocation] = useState(null);
  const [templateNames, setTemplateNames] = useState([]);

  const [imagingSchedule, setImagingSchedule] = useState(null);
  const [expNamePrefix, setExpNamePrefix] = useState(null);
  const [expNameSuffix, setExpNameSuffix] = useState(null);
  const [expNameFormatString, setExpNameFormatString] = useState(null);
  const [createNewExp, setCreateNewExp] = useState(true);
  const [allTemps, setAllTemps] = useState([]);
  const [wizardDataDirty, setWizardDataDirty] = useState(false);
  const [screenVolError, setScreenVolError] = useState<boolean>(false);
  const [proteinVolError, setProteinVolError] = useState<boolean>(false);
  const [expNameError, setExpNameError] = useState<boolean>(false);
  const explorerBroadcastChannel = useBroadcast(CHANNEL_TYPES.EXPLORER);
  const [activeStep, setActiveStep] = useState(WizardSteps.SelectPlateType);
  const [openState, setOpenState] = useState(true);

  useEffect(() => {
    async function fetchTemplateData() {
      if (expTemplate == null) {
        return;
      }
      const expWizTemplate = await rockFetchGet(
        `api/screenExpWizard/screenexperimenttemplate/${expTemplate}`,
        'get',
        undefined,
        true,
      );
      if (expWizTemplate != null) {
        const data = new ScreenExperimentTemplateDTO();
        data.init(expWizTemplate);
        setTemplateData(data);
      }
    }
    fetchTemplateData();
  }, [expTemplate]);

  useEffect(() => {
    if (!isNullOrUndefined(templateData)) {
      setScreens(templateData.screens);
      setProteins(templateData.proteinDetails);
      setPlateType(templateData.experimentPlateContainerInfo);
      setOwner(templateData.owner);
      setImagingSchedule(templateData.imagingScheduleInfo);
      setIncubationTemp(templateData.incubationTempratureIDs);
      setExpNamePrefix(templateData.experimentNamePrefix);
      setExpNameSuffix(templateData.experimentNameSuffix);
      setExpNameFormatString(templateData.experimentNameFormatString);
    }
  }, [templateData]);

  function isDisallowProtein() {
    const isNotNullProtein = proteins.find(
      (protein) => protein != null && protein?.name != null,
    );

    return isNullOrUndefined(isNotNullProtein) ? true : false;
  }

  useEffect(() => {
    if (createNewExp) {
      setTemplateData(null);
      setScreens([]);
      setProteins([]);
      setPlateType(getWizardDefaultPlateType());
      setExpTemplate(null);
      setOwner(null);
      setImagingSchedule(null);
      setIncubationTemp([]);
      setExpNamePrefix('');
      setExpNameSuffix('');
      setExpNameFormatString('');
    }
  }, [createNewExp]);

  const steps = [
    {
      id: WizardSteps.SelectPlateType,
      stepper: {
        title: 'SELECT PLATE TYPE',
        desc: 'Please Select Plate Type',
        disabled: false,
        error: false,
      },
      content: <SelectPlateType />,
      nextStepCallback: () => {
        setActiveStep((prevState) => prevState + 1);
      },
      backStepCallback: () => {
        // code to move to the previous step
      },
    },
    {
      id: WizardSteps.SelectScreen,
      stepper: {
        title: 'Select Screen',
        id: 'screenexperimentwizard-steps-selectscreen',
        desc: 'Please Select Screen',
        disabled: plateType?.id == -1 || (!createNewExp && expTemplate == null),
        error: false,
      },
      content: (
        <div id='screenexperimentwizard-steps-screen' style={{ height: '64vh' }}>
          <SelectScreenWizard
            id='screenexperimentwizard-steps-selectscreen'
            commonTreeNodes={commonTreeNodes}
            gSelectedNode={gSelectedNode}
            selectedParentNode={selectedParentNode}
          />
        </div>
      ),
      nextStepCallback: () => {
        setActiveStep((prevState) => prevState + 1);
      },
      backStepCallback: () => {
        setActiveStep((prevState) => prevState - 1);
      },
    },
    {
      id: WizardSteps.SelectProtein,
      stepper: {
        title: 'Select Protein',
        id: 'screenexperimentwizard-steps-selectprotein',
        desc: 'Please Select Protein',
        disabled: screens?.length == 0 || screenVolError,
        error: false,
      },
      content: (
        <div id='screenexperimentwizard-steps-protein' style={{ height: '64vh' }}>
          <SelectProteinWizard
            id='screenexperimentwizard-steps-selectprotein'
            commonTreeNodes={commonTreeNodes}
            gSelectedNode={gSelectedNode}
            selectedParentNode={selectedParentNode}
            plateType={plateType}
          />
        </div>
      ),
      nextStepCallback: () => {
        setActiveStep((prevState) => prevState + 1);
      },
      backStepCallback: () => {
        setActiveStep((prevState) => prevState - 1);
      },
    },
    {
      id: WizardSteps.DefineParameters,
      stepper: {
        title: 'Define Parameters',
        id: 'screenexperimentwizard-steps-defineparameters',
        desc: 'Please Define Parameters',
        disabled: isDisallowProtein() || proteinVolError,
        error: false,
      },
      content: (
        <div id='screenexperimentwizard-steps-defineparameter' className="h-[64vh]">
          <DefineParametersWizard id='screenexperimentwizard-steps-parameters' />
        </div>
      ),
      nextStepCallback: () => {
        setActiveStep((prevState) => prevState + 1);
      },
      backStepCallback: () => {
        setActiveStep((prevState) => prevState - 1);
      },
    },
    {
      id: WizardSteps.ReviewExperiment,
      stepper: {
        title: 'REVIEW EXPERIMENT(S)',
        id: 'screenexperimentwizard-steps-reviewexp',
        desc: 'Please review experiment(s)',
        disabled: incubationTemp.length == 0,
        error: false,
      },
      content: (
        <div id='screenexperimentwizard-steps-reviewexp' style={{ height: '64vh' }}>
          <ReviewExperiment
            id='screenexperimentwizard-steps-reviewexp'
            screens={screens}
            commonTreeNodes={commonTreeNodes}
            gSelectedNode={gSelectedNode}
            selectedParentNode={selectedParentNode}
          />
        </div>
      ),
      nextStepCallback: () => {
        setActiveStep((prevState) => prevState + 1);
      },
      backStepCallback: () => {
        setActiveStep((prevState) => prevState - 1);
      },
    },
    {
      id: WizardSteps.CreateExperiments,
      stepper: {
        title: 'CREATE EXPERIMENT(S)',
        id: 'screenexperimentwizard-steps-createexp',
        desc: 'Please create experiment(s)',
        disabled: expSaveLocation == null || expNameError,
        error: false,
      },
      content: <CreateExperiments id='screenexperimentwizard-steps-createexp' />,
      nextStepCallback: () => {
        setActiveStep((prevState) => prevState + 1);
      },
      backStepCallback: () => {
        setActiveStep((prevState) => prevState - 1);
      },
    },
  ];

  const nextButtonDisabled = useMemo(() => {
    switch (activeStep) {
      case WizardSteps.SelectPlateType:
        return plateType?.id == -1 || (!createNewExp && expTemplate == null);
      case WizardSteps.SelectScreen:
        return screens?.length == 0 || screenVolError;
      case WizardSteps.SelectProtein:
        return isDisallowProtein() || proteinVolError;
      case WizardSteps.DefineParameters:
        return incubationTemp.length == 0;
      case WizardSteps.ReviewExperiment:
        return expSaveLocation == null || expNameError;
      case WizardSteps.CreateExperiments:
        return false;
    }
  }, [
    activeStep,
    plateType,
    createNewExp,
    expTemplate,
    screens,
    screenVolError,
    proteins,
    proteinVolError,
    incubationTemp,
    expSaveLocation,
    expNameError,
  ]);

  const backButtonDisabled = useMemo(
    () => activeStep === WizardSteps.CreateExperiments,
    [activeStep],
  );

  const handleCloseClick = () => {
    onClose();
    setOpenState(false);
  };

  const handleStepClick = (stepIndex) => {
    setActiveStep(stepIndex);
  };

  const handleSkipClick = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep((prevState) => prevState + 1);
    }
  };

  const [owner, setOwner] = useState(null);
  function changeScreens(lScreens) {
    setScreens([...lScreens]);
  }
  function changeProteins(lProteins) {
    setProteins([...lProteins]);
  }
  async function saveScreenExpTemplate(templateName: string) {
    let templateNameWithFile = templateName;
    const xmlExist = templateName.includes('.xml');
    if (!xmlExist) {
      templateNameWithFile = `${templateName}.xml`;
    }
    let tempTemplateData = new ScreenExperimentTemplateDTO();
    if (!isNullOrUndefined(templateData) && !wizardDataDirty) {
      templateData.name = templateNameWithFile;
      tempTemplateData = templateData;
    } else {
      const containerPlate = new PlateTypeDTO();
      containerPlate.id = plateType.id;
      containerPlate.name = plateType.name;
      containerPlate.enabledDropNumbers = plateType.enabledDropNumbers;
      containerPlate.dropVolume = plateType.dropVolume;
      containerPlate.totalDrops = plateType.totalDrops;
      containerPlate.volume = plateType.volume;
      tempTemplateData.name = templateNameWithFile;
      const screenInfotemplate: ScreenInfoDTO[] = [];
      tempTemplateData.experimentPlateContainerInfo = plateType;
      screens.forEach((screen) => {
        const screeninfo = new ScreenInfoDTO();
        const screenlot = screen.screenLotRows.find(
          (screenlot) => screenlot.name == screen.defaultLotNumber,
        );
        screeninfo.screenLotId = isNullOrUndefined(screenlot) ? 0 : screenlot.id;
        screeninfo.name = screen.name;
        screeninfo.screenLotRows = screen.screenLotRows;
        screeninfo.id = screen.id;
        screeninfo.layerVolume = screen.layerVolume;
        screenInfotemplate.push(screeninfo);
      });
      tempTemplateData.screens = screens;
      const tempProtein = proteins
        .filter((protein) => protein)
        .map((p) => new ProteinDetailDTO({ ...p }));
      tempTemplateData.proteinDetails = tempProtein;
      tempTemplateData.owner = owner;
      tempTemplateData.experimentNamePrefix = expNamePrefix;
      tempTemplateData.experimentNameSuffix = expNameSuffix;
      tempTemplateData.experimentNameFormatString = expNameFormatString;
      tempTemplateData.experimentPlateContainerInfo = plateType;
      tempTemplateData.incubationTempratureIDs = incubationTemp;
      tempTemplateData.imagingScheduleInfo = imagingSchedule;
      tempTemplateData.canAddDropToContainer = true;
      tempTemplateData.setupTempratureId = 0;
      tempTemplateData.incubationTempratureID = 0;
    }

    const body = {
      fileName: templateNameWithFile,
      screenExperimentTemplate: tempTemplateData,
    };
    const response: boolean = await rockFetchPost(
      'api/screenexpwizard/savescreenexptemplate',
      body,
    );
    return response;
  }
  return (
    <ScreenWizardContext.Provider
      id={`screenexperimentwizard-context`}
      value={{
        createExpPath,
        setCreateExpPath,
        templateNames,
        setTemplateNames,
        screens,
        changeScreens,
        proteins,
        changeProteins,
        incubationTemp,
        setIncubationTemp,
        plateType,
        setPlateType,
        imagingSchedule,
        setImagingSchedule,
        owner,
        setOwner,
        expNamePrefix,
        setExpNamePrefix,
        expNameSuffix,
        setExpNameSuffix,
        expNameFormatString,
        setExpNameFormatString,
        createNewExp,
        setCreateNewExp,
        allTemps,
        setAllTemps,
        expTemplate,
        setExpTemplate,
        saveScreenExpTemplate,
        templateData,
        setTemplateData,
        wizardDataDirty,
        setWizardDataDirty,
        experimentInfoList,
        expSaveLocation,
        setExpSaveLocation,
        setScreenVolError,
        setProteinVolError,
        setExpNameError,
        explorerBroadcastChannel,
      }}
    >
      <FmlxWizard
        id='screenexperimentwizard-wizard'
        steps={steps}
        activeStep={activeStep}
        open={openState}
        onCloseClick={handleCloseClick}
        onStepClick={handleStepClick}
        onCancelClick={handleCloseClick}
        onSkipClick={handleSkipClick}
        disableNextButton={nextButtonDisabled}
        disableBackButton={backButtonDisabled}
        cancelable
        title="Screen Experiment Wizard"
        subtitle="Create Screen Experiment"
        type={FmlxWizard.Type.MODAL}
        modalSize={FmlxModal.Size.LARGE}
        id={id}
        onFinishClick={handleCloseClick}
      />
    </ScreenWizardContext.Provider>
  );
}

export default ScreenExperimentWizard;
